// admin-panel/src/components/layout/AdminLayout.jsx
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../lib/api';
import {
  LayoutDashboard, FolderOpen, MessageSquare, FileText, Settings2,
  Image, BookOpen, Star, Tag, Wrench, LogOut, Bell, Menu, X,
  ChevronRight, User, ExternalLink, PenSquare
} from 'lucide-react';

const NAV = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/inquiries',    icon: MessageSquare,   label: 'Inquiries',   badge: 'unread' },
  { to: '/quotes',       icon: FileText,        label: 'Quotes' },
  { to: '/projects',     icon: FolderOpen,      label: 'Projects' },
  { to: '/categories',   icon: Tag,             label: 'Categories' },
  { to: '/services',     icon: Wrench,          label: 'Services' },
  { to: '/testimonials', icon: Star,            label: 'Testimonials' },
  { to: '/blog',         icon: PenSquare,       label: 'Blog' },
  { to: '/media',        icon: Image,           label: 'Media Library' },
  { to: '/cms',          icon: BookOpen,        label: 'CMS / Pages' },
  { to: '/settings',     icon: Settings2,       label: 'Settings' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: notifs } = useQuery({
    queryKey: ['notifications'],
    queryFn: dashboardApi.notifications,
    refetchInterval: 30000,
  });

  const { data: stats } = useQuery({
    queryKey: ['inquiry-stats'],
    queryFn: () => import('../../lib/api').then(m => m.inquiriesApi.stats()),
    refetchInterval: 30000,
  });

  useEffect(() => { setSidebarOpen(false); }, [location]);

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white flex" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* ── Sidebar ── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#121212] border-r border-white/5 flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-[#8B5E3C] to-[#D4AF37] flex items-center justify-center text-xs font-bold">DW</div>
            <div>
              <div className="text-sm font-semibold text-white">Decentwoods</div>
              <div className="text-xs text-[#555]">Admin Panel</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {NAV.map(({ to, icon: Icon, label, badge }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all group ${
                isActive
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20'
                  : 'text-[#888] hover:text-white hover:bg-white/5'
              }`}>
              <Icon size={16} className="shrink-0" />
              <span className="text-sm font-medium flex-1">{label}</span>
              {badge === 'unread' && stats?.unread > 0 && (
                <span className="bg-[#D4AF37] text-black text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {stats.unread > 99 ? '99+' : stats.unread}
                </span>
              )}
              <ChevronRight size={12} className="opacity-0 group-hover:opacity-50 transition-opacity" />
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#8B5E3C]/30 border border-[#8B5E3C]/50 flex items-center justify-center">
              {user?.avatar ? <img src={user.avatar} className="w-full h-full rounded-full object-cover" /> : <User size={14} className="text-[#8B5E3C]" />}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-white truncate">{user?.name}</div>
              <div className="text-xs text-[#555] capitalize">{user?.role}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <a href="/" target="_blank"
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs text-[#888] hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-all">
              <ExternalLink size={12} /> View Site
            </a>
            <button onClick={logout}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs text-[#888] hover:text-red-400 bg-white/5 hover:bg-red-500/10 rounded-lg transition-all">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* ── Overlay ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main ── */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="h-16 bg-[#121212] border-b border-white/5 flex items-center justify-between px-6 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-400 hover:text-white">
            <Menu size={20} />
          </button>

          <div className="hidden lg:flex items-center gap-2 text-sm text-[#555]">
            <span className="capitalize">{location.pathname.split('/')[1] || 'Dashboard'}</span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Notifications */}
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)}
                className="relative w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                <Bell size={16} className="text-[#888]" />
                {notifs?.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D4AF37] rounded-full text-[10px] font-bold text-black flex items-center justify-center">
                    {notifs.unread_count}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-12 w-80 bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-3 border-b border-white/5 flex items-center justify-between">
                    <span className="text-sm font-semibold">Notifications</span>
                    <button onClick={() => setNotifOpen(false)}><X size={14} className="text-[#888]" /></button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs?.notifications?.length ? notifs.notifications.map((n) => (
                      <a key={n.id} href={n.link}
                        className="flex gap-3 p-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.type === 'inquiry' ? 'bg-blue-400' : 'bg-[#D4AF37]'}`} />
                        <div>
                          <div className="text-xs font-medium">{n.title}</div>
                          <div className="text-xs text-[#888] mt-0.5">{n.message}</div>
                          <div className="text-[11px] text-[#555] mt-1">{n.time}</div>
                        </div>
                      </a>
                    )) : (
                      <div className="p-6 text-center text-sm text-[#555]">No new notifications</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
